import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Clapperboard, Coins, FolderOpen, Home, PlusCircle } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { StudioList } from '@/components/layout/StudioList';
import { cn } from '@/lib/utils';
import { useReadyAlerts } from '@/lib/projects-api';

// THE PHONE TAB BAR (Studio Blueprint 02/03): five labelled tabs at the
// bottom of every page under md: Home, Watch, Create, Projects, Credits.
// Words under every icon (the blueprint: no icon without a visible label).
// Create opens the studio list as a sheet; Android Back closes the sheet
// (it pushes a history entry) instead of leaving the site.
//
// It sits above the page (z-40) and below dialogs and ChopBox's full screen
// mode (z-50+). The footer pads itself by --tabbar-h so nothing hides under it.

const TABS = [
  { key: 'home', to: '/', Icon: Home },
  { key: 'watch', to: '/watch', Icon: Clapperboard },
  { key: 'create', to: null, Icon: PlusCircle },
  { key: 'projects', to: '/projects', Icon: FolderOpen },
  { key: 'credits', to: '/pricing', Icon: Coins },
] as const;

export function TabBar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [createOpen, setCreateOpen] = useState(false);
  // Unseen "your render is ready" alerts, counted on the Projects tab (2026-10-16).
  const readyCount = useReadyAlerts().length;

  // Back closes the sheet: one history entry while it is open.
  useEffect(() => {
    if (!createOpen) return undefined;
    window.history.pushState({ ...(window.history.state || {}), tabSheet: true }, '');
    const onPop = () => setCreateOpen(false);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [createOpen]);

  const closeSheet = (viaLink: boolean) => {
    setCreateOpen(false);
    // Closed by a tap (not by Back): drop the entry we pushed, unless a link
    // is about to navigate anyway.
    if (!viaLink && window.history.state?.tabSheet) window.history.back();
  };

  const isActive = (to: string | null) => (to === '/' ? location.pathname === '/' : !!to && location.pathname.startsWith(to));

  return (
    <>
      <nav
        aria-label={t('shell.tabs.label')}
        data-testid="tab-bar"
        className="fixed inset-x-0 bottom-0 z-40 border-t border-white/10 bg-[rgba(8,10,14,0.96)] pb-[env(safe-area-inset-bottom)] backdrop-blur-xl md:hidden"
      >
        <ul className="mx-auto grid h-[var(--tabbar-h)] max-w-md grid-cols-5">
          {TABS.map(({ key, to, Icon }) => {
            const active = to ? isActive(to) : createOpen;
            const inner = (
              <>
                <span className="relative">
                  <Icon aria-hidden className={cn('h-[22px] w-[22px]', key === 'create' && 'text-primary')} />
                  {key === 'projects' && readyCount > 0 && (
                    <span
                      data-testid="tab-projects-badge"
                      className="absolute -right-2.5 -top-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-accent px-1 text-[11px] font-bold leading-none tabular-nums text-[#081014] ring-2 ring-[#080a0e]"
                    >
                      {readyCount > 9 ? '9+' : readyCount}
                      <span className="sr-only"> {t('projects.notice.badge', { count: readyCount })}</span>
                    </span>
                  )}
                </span>
                <span className="text-[11px] font-semibold leading-none">{t(`shell.tabs.${key}`)}</span>
              </>
            );
            const cls = cn(
              'flex h-full w-full flex-col items-center justify-center gap-1 transition-colors',
              active ? 'text-primary' : 'text-white/70 active:text-white',
            );
            return (
              <li key={key}>
                {to ? (
                  <Link to={to} className={cls} aria-current={active ? 'page' : undefined} data-testid={`tab-${key}`}>
                    {inner}
                  </Link>
                ) : (
                  <button type="button" className={cls} onClick={() => setCreateOpen(true)} aria-expanded={createOpen} data-testid={`tab-${key}`}>
                    {inner}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      </nav>
      <Sheet open={createOpen} onOpenChange={(open) => (open ? setCreateOpen(true) : closeSheet(false))}>
        <SheetContent side="bottom" className="max-h-[85dvh] overflow-y-auto rounded-t-3xl border-white/10 bg-[#0b0d12] pb-[calc(1.5rem+env(safe-area-inset-bottom))]">
          <SheetHeader className="text-left">
            <SheetTitle className="font-display text-xl">{t('shell.create.title')}</SheetTitle>
            <SheetDescription>{t('shell.create.lead')}</SheetDescription>
          </SheetHeader>
          <StudioList className="mt-4" replace onPick={() => closeSheet(true)} />
        </SheetContent>
      </Sheet>
    </>
  );
}
